import { useCallback, useEffect, useState } from 'react';
import { fetchSuperadminPayments, type SuperadminPaymentItem } from '../../features/superadmin/api/payments-list.api';
import type { ApiClient } from '../../shared/api/api-client';
import { formatOrgDate } from '../../shared/lib/format-org-datetime';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Spinner } from '../../shared/ui/Spinner';

function payStatusBadge(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'bg-emerald-600/15 text-emerald-700';
    case 'pending':
      return 'bg-amber-400/15 text-amber-700';
    case 'failed':
      return 'bg-red-500/15 text-red-700';
    case 'cancelled':
      return 'bg-[hsl(var(--ui-muted))] text-[hsl(var(--ui-muted-foreground))]';
    default:
      return 'bg-[hsl(var(--ui-muted))] text-[hsl(var(--ui-muted-foreground))]';
  }
}

function payStatusLabel(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'Успешно';
    case 'pending':
      return 'Ожидает';
    case 'failed':
      return 'Ошибка';
    case 'cancelled':
      return 'Отменён';
    default:
      return status;
  }
}

function formatRub(v: number): string {
  return `${v.toLocaleString('ru-RU')} ₽`;
}

export default function PaymentTableBlock({ apiClient }: { apiClient: ApiClient }) {
  const [items, setItems] = useState<SuperadminPaymentItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchSuperadminPayments(apiClient, { limit: '15', offset: '0' });
      setItems(d.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Последние платежи</CardTitle>
            <CardDescription>Успешные и недавние транзакции.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Spinner label="Загрузка платежей…" />
        ) : items.length === 0 ? (
          <p className="text-sm text-[hsl(var(--ui-muted-foreground))]">Нет платежей за последнее время.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[hsl(var(--ui-muted-foreground))]">
                  <th className="pb-1.5 pr-2 font-medium">ID</th>
                  <th className="pb-1.5 pr-2 font-medium">Организация</th>
                  <th className="pb-1.5 pr-2 font-medium text-right">Сумма</th>
                  <th className="pb-1.5 pr-2 font-medium">Провайдер</th>
                  <th className="pb-1.5 pr-2 font-medium">Статус</th>
                  <th className="pb-1.5 font-medium">Дата</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-t border-[hsl(var(--ui-border))]">
                    <td className="py-1.5 pr-2 tabular-nums text-[hsl(var(--ui-muted-foreground))]">{item.id}</td>
                    <td className="py-1.5 pr-2 max-w-[160px] truncate">{item.organization_id}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums font-medium">{formatRub(item.amount)}</td>
                    <td className="py-1.5 pr-2 capitalize">{item.provider}</td>
                    <td className="py-1.5 pr-2">
                      <span className={`inline-block rounded-sm px-1.5 py-0.5 text-xs font-medium leading-tight ${payStatusBadge(item.status)}`}>
                        {payStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className="py-1.5 whitespace-nowrap text-[hsl(var(--ui-muted-foreground))]">
                      {item.created_at ? formatOrgDate(item.created_at, undefined) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
